<script lang="ts" setup>
import type { PolygonInstanceRef } from '@/core/service/googleMaps/types';
import type { Ref } from 'vue';

import { isInsidePolygonBounds } from '@/core/service/googleMaps/utils';
import { mapPromiseKey } from '@/core/service/googleMaps/utils/symbols';
import { useGoogleMaps } from '~/composables/useGoogleMaps';

interface QuadrilateralProps {
	centerCoordinates: google.maps.LatLngLiteral | google.maps.LatLng | null;
	pathsCoordinates: (google.maps.LatLngLiteral | google.maps.LatLng)[];
	count?: number;
	groupKey: string;
	isSelected?: boolean;
	isClickable?: boolean;
}

const props = withDefaults(defineProps<QuadrilateralProps>(), {
	count: 0,
	isClickable: true
});

const emit = defineEmits<QuadrilateralEmits>();

interface QuadrilateralEmits {
	(
		e: 'click',
		value: { selected: boolean; groupKey: QuadrilateralProps['groupKey'] }
	): void;
}

const { api: googleMapsApi } = useGoogleMaps();
const mapPromise = inject<Promise<google.maps.Map | null> | null>(
	mapPromiseKey,
	null
);

type MapInstanceRef = Ref<google.maps.Map | undefined>;
type MarkerInstanceRef = Ref<
	google.maps.marker.AdvancedMarkerElement | undefined
>;

const mapInstance: MapInstanceRef = ref();
const polygonInstance: PolygonInstanceRef = ref();
const clusterInstance: MarkerInstanceRef = ref();

const isSelectedModel = defineModel<boolean>('isSelected');

const MIN_ZOOM_LEVEL = 13;

const rawMapInstance = computed(() => toRaw(mapInstance.value) || null);

const isUnmapped = computed(() => !props.count);

function setPolygonInstance() {
	if (!googleMapsApi.value) {
		return;
	}

	polygonInstance.value = new googleMapsApi.value.maps.Polygon({
		paths: props.pathsCoordinates,
		strokeColor: '#000000',
		strokeOpacity: 0,
		strokeWeight: 1,
		fillColor: '#000000',
		fillOpacity: 0,
		clickable: true
	});

	if (!isUnmapped.value) {
		polygonInstance.value?.setMap(rawMapInstance.value);
	}
}

function setPolygonInitialOptions() {
	polygonInstance.value?.setOptions({
		strokeOpacity: 0,
		fillOpacity: 0,
		strokeWeight: 0
	});
}

function setPolygonSelectedOptions() {
	polygonInstance.value?.setOptions({
		strokeOpacity: 1,
		strokeWeight: 2,
		fillOpacity: 0.1
	});
}

function setPolygonUnclickable() {
	polygonInstance.value?.setOptions({
		clickable: false
	});
}

function setPolygonClickable() {
	polygonInstance.value?.setOptions({
		clickable: true
	});
}

function areSameMapsValue(
	firstMap: google.maps.Map | null,
	secondMap: google.maps.Map | null
) {
	const isObject = (val: google.maps.Map | null) =>
		val !== null && typeof val === 'object';
	const areBothNull = firstMap === null && secondMap === null;

	return (isObject(firstMap) && isObject(secondMap)) || areBothNull;
}

function updatePolygonMapInstance(newMap: google.maps.Map | null) {
	const currentMap = polygonInstance.value?.getMap() || null;

	if (areSameMapsValue(currentMap, newMap)) {
		return;
	}

	toRaw(polygonInstance.value)?.setMap(newMap);
}

function createClusterElement(content: number): HTMLElement {
	const cluster = document.createElement('div');

	cluster.classList.add('previous-cluster');
	cluster.textContent = content.toString();

	return cluster;
}

function setClusterInstance() {
	if (!googleMapsApi.value || !props.centerCoordinates) {
		return;
	}

	const clusterEl = createClusterElement(props.count);

	clusterInstance.value = new googleMapsApi.value.marker.AdvancedMarkerElement({
		map: rawMapInstance.value,
		content: clusterEl,
		position: props.centerCoordinates,
		title: 'previous-cluster-'
	});

	if (clusterInstance.value) {
		clusterInstance.value.map = isUnmapped.value ? null : rawMapInstance.value;
	}
}

watch(
	() => props.count,
	count => {
		if (clusterInstance.value) {
			(clusterInstance.value.content as HTMLElement).textContent =
				count.toString();
		}

		if (count) {
			updateClusterMapInstance(rawMapInstance.value);
			updatePolygonMapInstance(rawMapInstance.value);
		}

		if (!count) {
			updateClusterMapInstance(null);
			updatePolygonMapInstance(null);
		}
	}
);

watch(
	() => props.isSelected,
	isSelectedFromProps => {
		if (isSelectedFromProps) {
			setPolygonSelectedOptions();
			setClusterSelectedClass();
			removeClusterHoverClass();
		} else {
			setPolygonInitialOptions();
			setClusterDeselectClass();
		}
	}
);

function setClusterSelectedClass() {
	(clusterInstance.value?.content as Element)?.classList.add('selected');
}

function setClusterDeselectClass() {
	(clusterInstance.value?.content as Element)?.classList.remove('selected');
}

function setClusterHoverClass() {
	(clusterInstance.value?.content as Element)?.classList.add('hovered');
}

function removeClusterHoverClass() {
	(clusterInstance.value?.content as Element)?.classList.remove('hovered');
}

function setClusterZoomOutClass() {
	(clusterInstance.value?.content as Element)?.classList.add(
		'zoom-out-previous-cluster'
	);
}

function removeClusterZoomOutClass() {
	(clusterInstance.value?.content as Element)?.classList.remove(
		'zoom-out-previous-cluster'
	);
}

function setClusterDefaultClass() {
	(clusterInstance.value?.content as Element)?.classList.add(
		'previous-cluster'
	);
}

function removeClusterDefaultClass() {
	(clusterInstance.value?.content as Element)?.classList.remove(
		'previous-cluster'
	);
}

function updateClusterMapInstance(newMap: google.maps.Map | null) {
	const currentMap = clusterInstance.value?.map || null;

	if (areSameMapsValue(currentMap, newMap) || !clusterInstance.value) {
		return;
	}

	clusterInstance.value.map = newMap;
}

function toggleSelection() {
	const zoom = mapInstance.value?.getZoom();
	const zoomOut = (zoom || 0) <= MIN_ZOOM_LEVEL;

	if (zoomOut) {
		return;
	}

	isSelectedModel.value = !isSelectedModel.value;

	if (isSelectedModel.value) {
		setPolygonSelectedOptions();
		setClusterSelectedClass();
		removeClusterHoverClass();
	} else {
		setClusterDeselectClass();
		setClusterHoverClass();
	}

	emit('click', { selected: isSelectedModel.value, groupKey: props.groupKey });
}

function toggleHover(event: google.maps.MapMouseEvent) {
	if (isSelectedModel.value || isUnmapped.value) {
		return;
	}

	const zoom = mapInstance.value?.getZoom();

	if (
		(zoom || 0) <= MIN_ZOOM_LEVEL ||
		!event?.latLng ||
		!polygonInstance.value
	) {
		return;
	}

	const isInsideBounds = isInsidePolygonBounds(
		polygonInstance.value,
		event.latLng
	);

	if (isInsideBounds) {
		updatePolygonMapInstance(rawMapInstance.value);
		setClusterHoverClass();
	}

	if (!isInsideBounds) {
		removeClusterHoverClass();
	}
}

function toggleZoomState() {
	const zoom: number = mapInstance.value?.getZoom() || 0;
	const zoomOut: boolean = zoom <= MIN_ZOOM_LEVEL;

	if (zoom) {
		if (zoomOut) {
			removeClusterDefaultClass();
			setClusterZoomOutClass();
			setPolygonInitialOptions();
			setPolygonUnclickable();
		}

		if (!zoomOut) {
			setClusterDefaultClass();
			removeClusterZoomOutClass();
			setPolygonClickable();

			if (isSelectedModel.value) {
				setPolygonSelectedOptions();
				setClusterSelectedClass();
			}
		}
	}
}

onBeforeMount(async () => {
	const map = mapPromise ? await mapPromise : null;
	mapInstance.value = map || undefined;

	setPolygonInstance();

	setClusterInstance();

	mapInstance.value?.addListener('zoom_changed', () => toggleZoomState());

	mapInstance.value?.addListener('idle', async () => {
		const bounds = mapInstance.value?.getBounds();
		const centerPosition = clusterInstance.value?.position;

		if (centerPosition) {
			const isInsideView = bounds?.contains(centerPosition);
			const map = isInsideView ? rawMapInstance.value : null;

			if (!isUnmapped.value) {
				updateClusterMapInstance(map);
				updatePolygonMapInstance(map);
			}
		}
	});

	mapInstance.value?.addListener('mousemove', toggleHover);
	polygonInstance.value?.addListener(
		'mousemove',
		(e: google.maps.MapMouseEvent) => {
			google.maps.event.trigger(mapInstance.value!, 'mousemove', e);
		}
	);

	if (polygonInstance.value) {
		google.maps.event.trigger(polygonInstance.value, 'mousemove');
	}

	polygonInstance.value?.addListener('gmp-click', () => {
		if (props.isClickable) {
			toggleSelection();
		}
	});

	clusterInstance.value?.addListener('gmp-click', () => {
		if (props.isClickable) {
			toggleSelection();
		}
	});

	toggleZoomState();
});

defineExpose({ clusterInstance, polygonInstance });
</script>

<template>
	<div></div>
</template>
